import type { AxiosError } from 'axios';
import { useRequest } from 'ahooks';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import posthog from 'posthog-js';
import ApiUsersManager from 'services/api/ApiUsersManager';
import { updateUserProperties } from 'services/PostHogIdentity';
import { userActions } from 'store/users';
import { toast } from 'sonner';

export function handleLicenseCodeError(error: unknown, t: TFunction): void {
  const maybeAxios = error as AxiosError | undefined;
  const status = maybeAxios?.response?.status;
  const data = (maybeAxios?.response?.data ?? {}) as { code?: string };

  if (status === 403) {
    switch (data.code) {
      case 'invalid_code':
        toast.error(t('settings.license.errors.invalidLicenseCode'));
        return;
      case 'contract_full':
        toast.error(t('settings.license.errors.contractFull'));
        return;
      case 'contract_expired':
        toast.error(t('settings.license.errors.contractExpired'));
        return;
      default:
        break;
    }
  }

  toast.error(t('settings.license.errors.default'));
}

type UseUpdateLicenseCodeOptions = {
  onSuccess?: () => void;
  posthogSource?: string;
};

export function useUpdateLicenseCode({ onSuccess, posthogSource }: UseUpdateLicenseCodeOptions) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return useRequest(
    (formValues: { licenseCode: string }) => ApiUsersManager.updateLicenseCode(formValues.licenseCode),
    {
      manual: true,
      onSuccess: async () => {
        try {
          const result = await ApiUsersManager.getUsersMe();
          dispatch(userActions.setCurrentUser({ ...result }));
          // The license code is a PostHog person property and a group, so it has
          // to be pushed even though the identity itself has not changed.
          updateUserProperties(result);
        } catch (e) {
          // Best-effort refresh; log but don't block success
          console.error('Failed to refresh current user after license update', e);
        }

        toast.success(t('settings.license.codeChanged'));

        if (posthogSource) {
          posthog.capture('settings.license_code_updated', { source: posthogSource });
        }

        onSuccess?.();
      },
      onError: error => {
        handleLicenseCodeError(error, t);
      },
    },
  );
}
